import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { Receipt, ArrowRight, TrendingDown } from "lucide-react";

/**
 * Compact TDS card shown on the Master Admin overview.
 * Shows this-month + FY totals with a shortcut into the full TDS Report tab.
 */
export default function TDSOverviewCard({ onNavigate }) {
  const [monthSummary, setMonthSummary] = useState(null);
  const [fySummary, setFySummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
    const monthEnd = today.toISOString().slice(0, 10);
    const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    const fyStart = `${fyStartYear}-04-01`;

    Promise.all([
      axios.get(`${API}/admin/tds/summary`, { params: { from: monthStart, to: monthEnd }, headers: getAuthHeaders() }),
      axios.get(`${API}/admin/tds/summary`, { params: { from: fyStart, to: monthEnd }, headers: getAuthHeaders() }),
    ])
      .then(([m, y]) => {
        setMonthSummary(m.data);
        setFySummary(y.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const monthTds = Number(monthSummary?.total_tds || 0);
  const fyTds = Number(fySummary?.total_tds || 0);
  const monthCount = monthSummary?.count || 0;
  const fyCount = fySummary?.count || 0;

  return (
    <div className="p-5 rounded-lg border border-white/10 bg-[#121212]/60" data-testid="tds-overview-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center">
            <Receipt size={16} className="text-[#00E5FF]" />
          </div>
          <div>
            <h3 className="text-sm font-black text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              TDS Deducted (Sec 194H)
            </h3>
            <p className="text-[10px] text-[#A1A1AA] mt-0.5">Payable to Income Tax</p>
          </div>
        </div>
        <button
          onClick={() => onNavigate?.("tds-report")}
          data-testid="tds-overview-view-report-btn"
          className="flex items-center gap-1 text-[10px] font-bold text-[#00E5FF] hover:text-white transition-colors"
        >
          Full report <ArrowRight size={11} />
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-[#A1A1AA]" data-testid="tds-overview-loading">Loading…</div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-md border border-emerald-500/25 bg-emerald-500/5">
            <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-300 mb-1.5">
              <TrendingDown size={10} /> This Month
            </div>
            <p className="text-xl font-black text-white font-mono" data-testid="tds-overview-month-total">
              ₹{monthTds.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </p>
            <p className="text-[9px] text-[#A1A1AA] mt-1">{monthCount} withdrawal{monthCount === 1 ? "" : "s"}</p>
          </div>
          <div className="p-3 rounded-md border border-white/10 bg-[#0A0A0A]">
            <div className="text-[9px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1.5">FY {new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1}–{(new Date().getMonth() >= 3 ? new Date().getFullYear() + 1 : new Date().getFullYear()) % 100}</div>
            <p className="text-xl font-black text-white font-mono" data-testid="tds-overview-fy-total">
              ₹{fyTds.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </p>
            <p className="text-[9px] text-[#A1A1AA] mt-1">{fyCount} withdrawal{fyCount === 1 ? "" : "s"}</p>
          </div>
        </div>
      )}
    </div>
  );
}
